import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useRecalls(options: {
  status?: string;
  severity?: string;
  page?: number;
  page_size?: number;
} = {}) {
  return useQuery({
    queryKey: ['recalls', options],
    queryFn: () => api.getRecalls(options),
    staleTime: 5 * 60 * 1000,
  });
}

export function useActiveRecalls() {
  return useQuery({
    queryKey: ['recalls', 'active'],
    queryFn: () => api.getActiveRecalls(),
    staleTime: 2 * 60 * 1000, // Refresh more often for active recalls
  });
}

export function useRecall(recallId: string) {
  return useQuery({
    queryKey: ['recall', recallId],
    queryFn: () => api.getRecall(recallId),
    enabled: !!recallId,
  });
}

export function useRecallItems(recallId: string, status?: string) {
  return useQuery({
    queryKey: ['recall-items', recallId, status],
    queryFn: () => api.getRecallItems(recallId, status),
    enabled: !!recallId,
  });
}

export function useRecallActions(recallId: string) {
  return useQuery({
    queryKey: ['recall-actions', recallId],
    queryFn: () => api.getRecallActions(recallId),
    enabled: !!recallId,
  });
}

export function useRecallStats() {
  return useQuery({
    queryKey: ['recall-stats'],
    queryFn: () => api.getRecallStats(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateRecall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createRecall(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recalls'] });
      queryClient.invalidateQueries({ queryKey: ['recall-stats'] });
    },
  });
}

export function useUpdateRecall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.updateRecall(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recalls'] });
      queryClient.invalidateQueries({ queryKey: ['recall'] });
    },
  });
}

export function useRecordRecallAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recallId, data }: { recallId: string; data: any }) => 
      api.recordRecallAction(recallId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recalls'] });
      queryClient.invalidateQueries({ queryKey: ['recall-items'] });
      queryClient.invalidateQueries({ queryKey: ['recall-actions'] });
      queryClient.invalidateQueries({ queryKey: ['recall-stats'] });
    },
  });
}
